/*
 * Live chat: Zendesk messaging, loaded when a shopper asks for it.
 *
 * Zendesk's widget is ~430 KB of script. The Zendesk Web Widget app embed
 * loaded it on every page for every visitor, though few ever open it, so the
 * app embed is off (config/settings_data.json) and the theme loads Zendesk's
 * snippet itself:
 *
 * - snippets/forcast-live-chat-launcher.liquid (rendered by the footer) draws a
 *   stand-in for Zendesk's launcher and carries the widget key. Pressing it
 *   loads the snippet and opens the messenger; once the messenger is open,
 *   Zendesk's own launcher takes over and the stand-in is hidden.
 * - Any other [data-forcast-live-chat] control (the contact page's "Message our
 *   live chat") does the same.
 * - A shopper who opened chat in the last 7 days has the widget loaded once the
 *   page is idle, wherever the launcher shows, so an agent's reply still puts
 *   an unread badge on the launcher.
 * - If the snippet cannot load (blocked, offline, no key) the control follows
 *   its data-fallback-href rather than doing nothing.
 *
 * Zendesk ships two widgets behind one global — messaging answers to
 * 'messenger', the classic Web Widget to 'webWidget' — and which one the store
 * runs is an admin setting the theme cannot read, so both are sent; the one not
 * running rejects the command and must not stop the other.
 *
 * Loaded from the launcher snippet and from sections/contact.liquid, so it can
 * be on a page twice; only the first copy runs.
 */
(function forcastLiveChat() {
  if (window.forcastLiveChatReady) return;
  window.forcastLiveChatReady = true;

  const OPENED_KEY = 'forcast-live-chat-opened';
  const KEEP_LOADED_FOR = 7 * 24 * 60 * 60 * 1000;
  /* How long a press waits for the snippet before giving up on it: 430 KB on
     a slow phone connection, with room to spare. */
  const LOAD_TIMEOUT = 15000;

  const launchers = Array.from(document.querySelectorAll('[data-forcast-live-chat-launcher]'));
  const key = launchers.length ? launchers[0].dataset.forcastLiveChatKey : '';

  const each = (callback) => {
    ['messenger', 'webWidget'].forEach((api) => {
      try {
        callback(api);
      } catch (error) {
        /* Not this store's widget flavour — the other call is the live one. */
      }
    });
  };
  const command = (action) => each((api) => window.zE(api, action));
  const on = (event, callback) => each((api) => window.zE(`${api}:on`, event, callback));

  const hideLaunchers = () => {
    launchers.forEach((launcher) => {
      launcher.hidden = true;
    });
  };

  const clearBusy = () => {
    document.querySelectorAll('[data-forcast-live-chat][aria-busy]').forEach((trigger) => {
      trigger.removeAttribute('aria-busy');
    });
  };

  const followFallback = (trigger) => {
    clearBusy();
    if (trigger.dataset.fallbackHref) window.location.href = trigger.dataset.fallbackHref;
  };

  /* Resolves once zE is defined. zE queues commands until the messenger is
     ready, so callers can send them straight away. */
  let loading = null;
  const load = () => {
    if (loading) return loading;
    loading = new Promise((resolve, reject) => {
      if (typeof window.zE === 'function') {
        resolve();
        return;
      }
      /* The app embed injects a script with this id. If it is still switched
         on (it lives in theme-editor state, which git does not reliably
         deploy), wait for that copy rather than loading a second one. */
      let script = document.getElementById('ze-snippet');
      if (!script) {
        if (!key) {
          reject(new Error('No Zendesk widget key'));
          return;
        }
        script = document.createElement('script');
        script.id = 'ze-snippet';
        script.src = `https://static.zdassets.com/ekr/snippet.js?key=${encodeURIComponent(key)}`;
        document.head.appendChild(script);
      }
      script.addEventListener('load', () => resolve());
      script.addEventListener('error', () => reject(new Error('Zendesk snippet failed to load')));
    }).then(() => {
      on('open', () => {
        clearBusy();
        hideLaunchers();
      });
      /* assets/forcast-hide-support-widget.js waits for this on product pages. */
      document.dispatchEvent(new CustomEvent('forcast:live-chat:loaded'));
    });
    return loading;
  };

  const remember = () => {
    try {
      window.localStorage.setItem(OPENED_KEY, String(Date.now()));
    } catch (error) {
      /* Storage blocked (private mode): the next page just waits for a press. */
    }
  };

  const openedRecently = () => {
    try {
      return Date.now() - Number(window.localStorage.getItem(OPENED_KEY)) < KEEP_LOADED_FOR;
    } catch (error) {
      return false;
    }
  };

  const open = (trigger) => {
    remember();
    trigger.setAttribute('aria-busy', 'true');
    const stalled = window.setTimeout(() => followFallback(trigger), LOAD_TIMEOUT);
    load()
      .then(() => {
        window.clearTimeout(stalled);
        command('show');
        command('open');
      })
      .catch(() => {
        window.clearTimeout(stalled);
        followFallback(trigger);
      });
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-forcast-live-chat]');
    if (!trigger) return;
    event.preventDefault();
    open(trigger);
  });

  const whenIdle = (callback) => {
    const run = () => {
      if ('requestIdleCallback' in window) window.requestIdleCallback(callback);
      else window.setTimeout(callback, 0);
    };
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  };

  /* A launcher hidden by CSS (the phone-width product page) has no boxes.
     There is nothing to badge there, so the widget is not loaded for it. */
  const launcherShowing = () => launchers.some((launcher) => launcher.getClientRects().length > 0);

  if (typeof window.zE === 'function' || document.getElementById('ze-snippet')) {
    /* The app embed is still on, so Zendesk's own launcher is coming anyway. */
    hideLaunchers();
    load().catch(() => {});
  } else if (openedRecently() && launcherShowing()) {
    whenIdle(() => {
      load()
        .then(hideLaunchers)
        .catch(() => {});
    });
  }
})();
